import React, { useState, useEffect, useRef } from "react";
import {
  Col,
  Row,
  Button,
  Card,
  CardBody,
  Badge,
  Alert,
} from "reactstrap";
import Widget from "../../components/Widget/Widget.js";
import { getItemByBarcode, updateStockByBarcode } from "../../services/inventoryService.js";
import { BrowserMultiFormatReader } from '@zxing/library';

import s from "./Scan.module.scss";

const Scan = function () {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedItem, setScannedItem] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [detectedBarcode, setDetectedBarcode] = useState("");
  const [scanOverlay, setScanOverlay] = useState({ show: false, barcode: "", attempts: 0 });
  const [scanDebug, setScanDebug] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());

  // For testing - manual barcode input
  const [manualBarcode, setManualBarcode] = useState("");
  
  // Check if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Start camera for scanning
  const startScanning = async () => {
    try {
      setError("");
      setScanDebug([]);
      
      // Try back camera first on mobile, then fall back to any available camera
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Prefer back camera
          width: { ideal: 640, max: 1920 },
          height: { ideal: 480, max: 1080 }
        }
      };
      
      let stream;
      try {
        // First try to get back camera
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        setScanDebug(prev => [...prev, 'Back camera accessed successfully']);
      } catch (backCameraError) {
        console.warn('Back camera failed, trying any available camera:', backCameraError);
        // Fall back to any available camera
        try {
          const fallbackConstraints = {
            video: {
              width: { ideal: 640, max: 1920 },
              height: { ideal: 480, max: 1080 }
            }
          };
          stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          setScanDebug(prev => [...prev, 'Using available camera (not back camera)']);
        } catch (anyError) {
          throw anyError;
        }
      }
      
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsScanning(true);
        
        // Force video to play
        videoRef.current.muted = true; // Ensure muted for autoplay
        videoRef.current.playsInline = true;
        
        setScanDebug(prev => [...prev, 'Video stream connected, starting playback...']);
        
        // Start video playback immediately
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log('Video playing successfully');
            setScanDebug(prev => [...prev, 'Video playing - scanner starting...']);
            
            // Start scanning after a short delay
            setTimeout(() => {
              scanIntervalRef.current = setInterval(() => {
                captureAndScan();
              }, 1000); // 1 second intervals for stability
            }, 1000);
            
          }).catch((e) => {
            console.error('Video play failed:', e);
            setError("Video playback failed. Please try again.");
          });
        }
      }
    } catch (err) {
      console.error("Camera error:", err);
      if (err.name === 'NotAllowedError') {
        setError("Camera access denied. Please allow camera access in your browser settings and try again.");
      } else if (err.name === 'NotFoundError') {
        setError("No camera found. Please use a device with a camera.");
      } else if (err.name === 'OverconstrainedError') {
        setError("Camera constraints not supported. Trying with basic settings...");
        // Could add a retry with simpler constraints here
      } else {
        setError(`Camera error: ${err.message}. Use manual input instead.`);
      }
    }
  };

  // Stop camera
  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setIsScanning(false);
    setScanOverlay({ show: false, barcode: "" });
  };

  // Capture frame and attempt to scan for barcode
  const captureAndScan = async () => {
    if (!videoRef.current || !canvasRef.current || !isScanning) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Check if video is actually playing and has dimensions
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      setScanDebug(prev => [...prev.slice(-4), 'Video not ready - waiting...']);
      return;
    }

    try {
      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw current video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Update scan attempts counter
      const attempts = scanOverlay.attempts + 1;
      
      // Try to decode barcode from the video element directly first
      try {
        const result = await codeReader.current.decodeFromVideoDevice();
        if (result && result.text) {
          handleBarcodeFound(result.text);
          return;
        }
      } catch (e) {
        // Fall back to canvas-based detection
      }
      
      // Attempt to decode barcode from canvas
      const result = await codeReader.current.decodeFromCanvas(canvas);
      
      if (result && result.text) {
        handleBarcodeFound(result.text);
      } else {
        // Update scanning status
        setScanOverlay({ 
          show: true, 
          barcode: `🔍 Scanning... (${attempts} attempts)`,
          attempts: attempts
        });
        
        // Add to debug log every 10 attempts
        if (attempts % 10 === 0) {
          setScanDebug(prev => [...prev.slice(-4), `${attempts} scan attempts - keep trying`]);
        }
      }
      
    } catch (error) {
      // Update scan attempts even on error
      const attempts = scanOverlay.attempts + 1;
      setScanOverlay({ 
        show: true, 
        barcode: `🔍 Scanning... (${attempts} attempts)`,
        attempts: attempts
      });
      
      // Debug specific errors
      if (attempts % 20 === 0) {
        setScanDebug(prev => [...prev.slice(-4), `Scan error: ${error.message}`]);
        console.log('Scanning error:', error);
      }
    }
  };
  
  // Handle successful barcode detection
  const handleBarcodeFound = async (barcodeText) => {
    console.log('Barcode detected:', barcodeText);
    
    setScanOverlay({ 
      show: true, 
      barcode: `✅ Found: ${barcodeText}`,
      attempts: scanOverlay.attempts + 1
    });
    
    // Add to debug log
    setScanDebug(prev => [...prev.slice(-4), `SUCCESS: Found barcode ${barcodeText}`]);
    
    // Stop scanning and lookup the item
    stopScanning();
    await lookupBarcode(barcodeText);
  };

  // Handle manual barcode input for testing
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (manualBarcode.trim()) {
      await lookupBarcode(manualBarcode.trim());
      setManualBarcode("");
    }
  };

  // Look up item by barcode
  const lookupBarcode = async (barcode) => {
    setMessage("");
    setError("");
    
    try {
      const result = await getItemByBarcode(barcode);
      
      if (result.error) {
        setError(`Item not found: ${barcode}`);
        setScannedItem(null);
      } else {
        setScannedItem(result);
        setMessage(`Found: ${result.item_name}`);
      }
    } catch (err) {
      setError("Failed to lookup barcode");
      setScannedItem(null);
    }
  };

  // Use one item (subtract 1 from stock)
  const useItem = async () => {
    if (!scannedItem) return;
    
    const newStock = Math.max(0, scannedItem.current_stock - 1);
    
    try {
      const result = await updateStockByBarcode(scannedItem.barcode, newStock);
      
      if (result.success) {
        setMessage(`✅ Used 1 ${scannedItem.item_name}. Stock: ${scannedItem.current_stock} → ${newStock}`);
        // Update local item to show new stock
        setScannedItem(prev => ({
          ...prev,
          current_stock: newStock
        }));
      } else {
        setError("Failed to update stock");
      }
    } catch (err) {
      setError("Failed to update stock");
    }
  };

  // Add item (add 1 to stock)
  const addItem = async () => {
    if (!scannedItem) return;
    
    const newStock = scannedItem.current_stock + 1;
    
    try {
      const result = await updateStockByBarcode(scannedItem.barcode, newStock);
      
      if (result.success) {
        setMessage(`✅ Added 1 ${scannedItem.item_name}. Stock: ${scannedItem.current_stock} → ${newStock}`);
        setScannedItem(prev => ({
          ...prev,
          current_stock: newStock
        }));
      } else {
        setError("Failed to update stock");
      }
    } catch (err) {
      setError("Failed to update stock");
    }
  };

  // Get stock status color
  const getStockStatus = (current, min) => {
    if (current <= min) return "danger";
    if (current <= min * 1.5) return "warning";
    return "success";
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  return (
    <div className={s.scanPage}>
      <Row>
        <Col>
          <Widget>
            <div className="text-center mb-4">
              <h2 className="headline-2">Barcode Scanner</h2>
              <p className="body-2 text-muted">
                {isMobile 
                  ? "Use your phone's camera to scan item barcodes and update inventory instantly" 
                  : "Mobile device recommended for best scanning experience"
                }
              </p>
              {!isMobile && (
                <div className="alert alert-info">
                  📱 <strong>Tip:</strong> For better barcode scanning, use this feature on a mobile device with a rear camera.
                </div>
              )}
            </div>

            {/* Camera Section */}
            <div className="text-center mb-4">
              {!isScanning ? (
                <Button 
                  color="primary" 
                  size="lg"
                  onClick={startScanning}
                  className="mb-3"
                >
                  📷 Start Scanning
                </Button>
              ) : (
                <div className={s.cameraContainer}>
                  <div className={s.videoWrapper}>
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      playsInline
                      muted
                      controls={false}
                      style={{ 
                        width: '100%', 
                        maxWidth: '100vw', 
                        height: isMobile ? '280px' : '320px', 
                        objectFit: 'cover',
                        borderRadius: '12px',
                        border: '3px solid #28a745',
                        backgroundColor: '#000',
                        display: 'block',
                        margin: '0 auto'
                      }}
                      className="mb-3"
                    />
                    
                    {/* Scanning overlay */}
                    <div className={s.scanOverlay}>
                      <div className={s.scanFrame}></div>
                      {scanOverlay.show && (
                        <div className={s.scanFeedback}>
                          <div className="bg-primary text-white p-2 rounded">
                            {scanOverlay.barcode}
                            <br />
                            <small>Point camera at barcode</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button color="secondary" onClick={stopScanning}>
                    Stop Camera
                  </Button>
                  
                  <div className="mt-2">
                    <small className="text-muted">
                      💡 {isMobile 
                          ? "Hold barcode 6-12 inches from camera and keep steady" 
                          : "Position barcode clearly in front of camera"
                        }
                    </small>
                  </div>
                </div>
              )}
            </div>

            {/* Debug Panel - Show what scanner sees */}
            {isScanning && (
              <div className="mb-4">
                <h5>Scanner Debug</h5>
                <div className="bg-light p-3 rounded">
                  <p><strong>Device:</strong> {isMobile ? 'Mobile' : 'Desktop'}</p>
                  <p><strong>Current Status:</strong> {scanOverlay.barcode || "Starting scanner..."}</p>
                  <p><strong>Scan Attempts:</strong> {scanOverlay.attempts}</p>
                  <p><strong>Video Ready:</strong> {videoRef.current ? (videoRef.current.readyState > 0 ? 'Yes' : 'No') : 'No'}</p>
                  <p><strong>Video Playing:</strong> {videoRef.current ? (!videoRef.current.paused ? 'Yes' : 'No') : 'No'}</p>
                  <p><strong>Camera Resolution:</strong> {videoRef.current ? `${videoRef.current.videoWidth}x${videoRef.current.videoHeight}` : "Loading..."}</p>
                  <p><strong>Video Dimensions:</strong> {videoRef.current ? `${videoRef.current.clientWidth}x${videoRef.current.clientHeight}` : "Loading..."}</p>
                  
                  {scanDebug.length > 0 && (
                    <div>
                      <strong>Recent Activity:</strong>
                      <ul className="mb-0 mt-2">
                        {scanDebug.map((log, index) => (
                          <li key={index} className="text-muted small">{log}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Manual Input for Testing */}
            <div className="mb-4">
              <h5>Manual Input (for testing)</h5>
              <form onSubmit={handleManualSubmit} className="d-flex">
                <input
                  type="text"
                  className="form-control mr-2"
                  placeholder="Enter barcode manually..."
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                />
                <Button type="submit" color="info">
                  Lookup
                </Button>
              </form>
            </div>

            {/* Messages */}
            {error && (
              <Alert color="danger" className="mb-4">
                {error}
              </Alert>
            )}
            
            {message && (
              <Alert color="success" className="mb-4">
                {message}
              </Alert>
            )}

            {/* Scanned Item Display */}
            {scannedItem && (
              <Card className="mb-4">
                <CardBody>
                  <h4>{scannedItem.item_name}</h4>
                  <div className="mb-3">
                    <Badge color={getStockStatus(scannedItem.current_stock, scannedItem.min_stock)}>
                      Stock: {scannedItem.current_stock} {scannedItem.unit}
                    </Badge>
                    <Badge color="secondary" className="ml-2">
                      {scannedItem.category}
                    </Badge>
                  </div>
                  <p><strong>Location:</strong> {scannedItem.location}</p>
                  <p><strong>Min Stock:</strong> {scannedItem.min_stock}</p>
                  <p><strong>Barcode:</strong> {scannedItem.barcode}</p>
                  
                  <div className="d-flex justify-content-center mt-4">
                    <Button 
                      color="warning" 
                      size="lg" 
                      className="mr-3"
                      onClick={useItem}
                      disabled={scannedItem.current_stock <= 0}
                    >
                      Use Item (-1)
                    </Button>
                    <Button 
                      color="success" 
                      size="lg"
                      onClick={addItem}
                    >
                      Add Item (+1)
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )}

            {!scannedItem && !error && !message && (
              <div className="text-center text-muted">
                <p>Scan a barcode or enter one manually to get started</p>
              </div>
            )}
          </Widget>
        </Col>
      </Row>
      
      {/* Show canvas so you can see what camera captures */}
      {isScanning && (
        <Row className="mt-4">
          <Col>
            <div className="text-center">
              <h5>What the camera sees:</h5>
              <canvas 
                ref={canvasRef} 
                style={{ 
                  border: '2px solid #007bff',
                  borderRadius: '8px',
                  maxWidth: '100%',
                  height: 'auto'
                }} 
              />
              <p className="text-muted mt-2">
                <small>This shows the actual frame being analyzed for barcodes</small>
              </p>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Scan;